/**
 * Octo AI uçları (Octo.Dal: AiChatController + ClaudeCodeController).
 *
 * Uçların tamamı hazırdı — masaüstü kabuğu (Blazor `AiChatPanel`) ve mobil uygulama
 * aynılarını kullanıyor; burada yeni uç yazılmadı.
 *
 * Yetki üç ayrı şeye bağlı ve üçü de ayrı ayrı sorulmalı:
 *   1. Kullanıcının AI sohbet yetkisi (`user.canUseAiChat`, giriş yanıtından gelir),
 *   2. Sunucuda Claude Code'un çalışır olması (`/api/claudecode/status`),
 *   3. Token kotası (merkezî havuz, CustomerProductId bazında).
 * Üçünden biri eksikken paneli açmak, kullanıcıyı ancak mesaj gönderdiğinde öğrendiği
 * bir duvara götürüyordu.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/shared/api/http/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import type { AiAttachmentRef, AiConversation, AiMessageDto, AiQuota, AiStatus } from '@/shared/types/aiChat';

export const aiKeys = {
  status: ['ai', 'status'] as const,
  quota: (userId: string) => ['ai', 'quota', userId] as const,
  conversations: (userId: string) => ['ai', 'conversations', userId] as const,
  messages: (conversationId: string) => ['ai', 'messages', conversationId] as const,
  instructions: ['ai', 'instructions'] as const,
};

/**
 * Sunucuda Claude Code var mı?
 *
 * Kurulum başına sabit bir yanıt: uzun önbellekli, oturum boyunca bir kez sorulur.
 * Yalnızca yetkisi olan kullanıcıya sorulur — yetkisizde istek bile atılmaz.
 */
export function useAiStatus(enabled: boolean) {
  return useQuery({
    queryKey: aiKeys.status,
    queryFn: () => apiClient.get<AiStatus>(ENDPOINTS.AI_STATUS, { octo: { expectSuccess: false } }),
    enabled,
    staleTime: 30 * 60_000,
    retry: false,
  });
}

/** Token kotası (merkezî havuz). Panel açılınca ve her yanıttan sonra tazelenir. */
export function useAiQuota(userId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: aiKeys.quota(userId ?? ''),
    queryFn: () => apiClient.get<AiQuota>(ENDPOINTS.AI_QUOTA(userId!), { octo: { expectSuccess: false } }),
    enabled: enabled && !!userId,
    staleTime: 30_000,
    retry: false,
  });
}

export function useAiConversations(userId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: aiKeys.conversations(userId ?? ''),
    queryFn: () => apiClient.get<AiConversation[]>(ENDPOINTS.AI_CONVERSATIONS(userId!)),
    enabled: enabled && !!userId,
    staleTime: 60_000,
  });
}

export function useAiMessages(conversationId: string | null) {
  return useQuery({
    queryKey: aiKeys.messages(conversationId ?? ''),
    queryFn: () => apiClient.get<AiMessageDto[]>(ENDPOINTS.AI_CONVERSATION_MESSAGES(conversationId!)),
    enabled: !!conversationId,
    staleTime: 60_000,
  });
}

export function useRenameConversation(userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, title }: { conversationId: string; title: string }) =>
      apiClient.put(ENDPOINTS.AI_CONVERSATION_RENAME(conversationId), { title }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiKeys.conversations(userId ?? '') });
    },
  });
}

export function useDeleteConversation(userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) =>
      apiClient.delete(ENDPOINTS.AI_CONVERSATION_DELETE(conversationId)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiKeys.conversations(userId ?? '') });
    },
  });
}

export interface AiUploadResult extends Partial<AiAttachmentRef> {
  success?: boolean;
  message?: string;
  [key: string]: unknown;
}

/**
 * Dosya eki: içerik base64 olarak gönderilir, sunucu diske yazıp yolunu döner —
 * Claude o yolu okuyor (`--add-dir` + Read izni).
 */
export async function uploadAiAttachment(file: File): Promise<AiAttachmentRef> {
  const base64 = await fileToBase64(file);
  const res = await apiClient.post<AiUploadResult>(
    ENDPOINTS.AI_UPLOAD_ATTACHMENT,
    // Sunucu sözleşmesi (AiUploadAttachmentRequest): fileName + base64Content + contentType.
    {
      fileName: file.name,
      base64Content: base64,
      contentType: file.type || 'application/octet-stream',
    },
    { octo: { expectSuccess: false } },
  );
  if (res?.success === false || !res?.filePath) {
    throw new Error(res?.message || 'Dosya yüklenemedi.');
  }
  return {
    fileName: res.fileName ?? file.name,
    filePath: res.filePath,
    contentType: res.contentType ?? file.type ?? 'application/octet-stream',
    fileSize: res.fileSize ?? file.size,
  };
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('Dosya okunamadı.'));
    reader.readAsDataURL(file);
  });
}
